import React from 'react';
import {
    Grid,
    GridItem,
    HStack,
    VStack,
    Text,
    Avatar,
    IconButton,
    Icon,
    Button,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Box,
    Textarea,
    Badge,
    Divider,
    ButtonGroup,
    chakra,
    Center,
    StackDivider,
    Tooltip,
} from '@chakra-ui/react';
import { HiOutlinePhone, HiOutlineDotsHorizontal } from 'react-icons/hi';
import { MdOutlineHeadphones, MdSettings } from 'react-icons/md';
import { GoWorkflow } from 'react-icons/go';
import { IntegrationViewBaseConfig } from '../../../base';
import { ThreadCommonViewProps } from './threadCommonProps';
import { CallOverlayContainerProps } from '../../call/overlay/callOverlayProps';
import {
    ThreadContextContainerProps,
    ThreadOptionContainerProps,
} from '../../../../chat';
import { ComposerContainerProps } from '../../../../chat';
import { CollectionContextContainerProps } from '../../../../chat/composer/context/select/option/collection';
import { SolutionContextContainerProps } from '../../../../chat/composer/context/select/option/solution';
import { ThreadContextItemContainerProps } from '../../../../chat';
import {
    MessageContextItemComponents,
    MessageContextItemContainerProps,
} from '../../../../chat/message';
import { createThreadCommonComposerView } from './composer';
import {
    MessageItemContainerProps,
    MessageItemViewProps,
} from '../../../../chat/message/item';
import {
    MessageGroupContainerProps,
    MessageGroupViewProps,
} from '../../../../chat/group';
import {
    MessageSectionContainerProps,
    MessageSectionViewProps,
} from '../../../../chat/section';
import { MessagePartComponents, MessagePartContainerProps } from '../../../../chat/part';
import { assertNever } from '@varos/util-typescript';
import { CallRequestOverlayContainerProps } from '../../../../chat/call';

// Sticky date header component
interface StickyDateHeaderProps {
    dateLabel: string;
    isSticky?: boolean;
}

function StickyDateHeader({ dateLabel, isSticky = false }: StickyDateHeaderProps) {
    return (
        <HStack
            userSelect="none"
            position="sticky"
            top={0}
            justify="center"
            w="full"
            zIndex={10}
            // py={2}
        >
            <Box
                bg="black"
                borderRadius="full"
                borderWidth={2}
                borderColor="whiteAlpha.200"
                boxShadow="lg"
                overflow="hidden"
            >
                <Badge
                    px={4}
                    py={1}
                    // bg="whiteAlpha.200"
                    bg="whiteAlpha.50"
                    fontSize="xs"
                    color="whiteAlpha.600"
                >
                    {dateLabel}
                </Badge>
            </Box>
        </HStack>
    );
}

interface MessageBubbleProps {
    content: string;
}

function MessageBubble({ content }: MessageBubbleProps) {
    // Don't render empty message bubbles
    if (!content?.trim()) {
        return null;
    }

    return (
        <HStack w="full" align="start" spacing={0} position="relative">
            <HStack align="start" spacing={3} w="full">
                <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Box w="full">
                        <Text
                            fontSize="md"
                            lineHeight="1.6"
                            color="whiteAlpha.700"
                            wordBreak="break-word"
                            whiteSpace="pre-wrap"
                        >
                            {content}
                        </Text>
                    </Box>
                </VStack>
            </HStack>
        </HStack>
    );
}

function TypingIndicator({ name }: { name: string }) {
    return (
        <Text
            position="absolute"
            top={-6}
            left={4}
            fontSize="sm"
            color="whiteAlpha.500"
            fontStyle="italic"
            fontWeight="medium"
            px={2}
            py={1}
            borderRadius="sm"
            zIndex={10}
        >
            {name} is typing...
        </Text>
    );
}

export function createThreadCommonView(
    config: IntegrationViewBaseConfig & {
        Container: {
            CallOverlay: React.FC<CallOverlayContainerProps>;
            ThreadOption: React.FC<ThreadOptionContainerProps>;
            ThreadContext: React.FC<ThreadContextContainerProps>;
            Call: {
                Request: {
                    Overlay: React.FC<CallRequestOverlayContainerProps>;
                };
            };
            Message: {
                Section: React.FC<MessageSectionContainerProps>;
                Group: React.FC<MessageGroupContainerProps>;
                Item: React.FC<MessageItemContainerProps>;
                Part: React.FC<MessagePartContainerProps>;
                Context: {
                    Item: React.FC<MessageContextItemContainerProps>;
                };
            };
            Context: {
                Select: {
                    Collection: React.FC<CollectionContextContainerProps>;
                    Solution: React.FC<SolutionContextContainerProps>;
                };
                Item: React.FC<ThreadContextItemContainerProps>;
            };
            Composer: React.FC<ComposerContainerProps>;
        };
    }
): React.FC<ThreadCommonViewProps> {
    const {
        Container: {
            CallOverlay: CallOverlayContainer,
            Composer: ComposerContainer,
            Message: {
                Item: MessageItemContainer,
                Group: MessageGroupContainer,
                Section: MessageSectionContainer,
                Part: MessagePartContainer,
                Context: { Item: MessageContextItemContainer },
            },
            Call: {
                Request: { Overlay: CallRequestOverlayContainer },
            },
        },
    } = config;

    const Composer = createThreadCommonComposerView(config);

    const MessageContextItemComponents: MessageContextItemComponents = {
        Solution(props) {
            return (
                <HStack
                    p={0}
                    bg="none"
                    minH={0}
                    h="inherit"
                    _hover={{ bg: 'whiteAlpha.50' }}
                    borderRadius="lg"
                    borderWidth={2}
                    borderColor="whiteAlpha.200"
                    cursor="pointer"
                    w="full"
                    maxW="24rem"
                    spacing={3}
                    overflow="hidden"
                    userSelect="none"
                >
                    <Center h={8} w={8} bg="whiteAlpha.100" flexShrink={0}>
                        {props.item.icon.kind === 'emoji' ? (
                            <Text fontSize="lg">{props.item.icon.text}</Text>
                        ) : (
                            <Icon as={props.item.icon.Icon} fontSize="lg" />
                        )}
                    </Center>
                    <HStack w="full" justify="space-between" align="center">
                        <Text
                            color="whiteAlpha.800"
                            fontWeight="semibold"
                            noOfLines={1}
                            fontSize="sm"
                        >
                            {props.item.label}
                        </Text>
                        <Badge bg="none" size="xs" px={3} color="whiteAlpha.500">
                            Solution
                        </Badge>
                    </HStack>
                </HStack>
            );
        },
    };

    const MessagePartComponents: MessagePartComponents = {
        Text(props) {
            return (
                <HStack w="full" align="start" spacing={0} position="relative">
                    <HStack align="start" spacing={3} w="full">
                        <VStack align="start" spacing={1} flex={1} minW={0}>
                            <Box w="full">
                                <Text
                                    fontSize="md"
                                    // lineHeight="1.6"
                                    color="whiteAlpha.700"
                                    wordBreak="break-word"
                                    whiteSpace="pre-wrap"
                                >
                                    {props.text}
                                </Text>
                            </Box>
                        </VStack>
                    </HStack>
                </HStack>
            );
        },
        CallTool(props) {
            const { status } = props;
            if (status.kind === 'ended') {
                return (
                    <HStack
                        align="center"
                        spacing={2}
                        w="full"
                        borderWidth={2}
                        borderColor="whiteAlpha.200"
                        borderRadius="lg"
                        h={10}
                        px={3}
                        color="whiteAlpha.500"
                        fontWeight="medium"
                        maxW="24rem"
                        fontSize="sm"
                    >
                        <Icon as={status.Icon} fontSize="lg" color="whiteAlpha.400" />
                        <Text lineHeight="1.6">{status.duration.label}</Text>
                    </HStack>
                );
            }
            if (status.kind === 'rejected' || status.kind === 'timeout') {
                return (
                    <HStack
                        align="center"
                        spacing={2}
                        w="full"
                        borderWidth={2}
                        borderColor="whiteAlpha.200"
                        borderRadius="lg"
                        h={10}
                        px={3}
                        color="whiteAlpha.500"
                        fontWeight="medium"
                        maxW="24rem"
                        fontSize="sm"
                    >
                        <Icon as={status.Icon} fontSize="lg" color="whiteAlpha.400" />
                        <Text lineHeight="1.6">{status.description}</Text>
                    </HStack>
                );
            }
            if (status.kind === 'pending') {
                return (
                    <>
                        <VStack
                            align="stretch"
                            spacing={0}
                            w="full"
                            maxW="24rem"
                            borderWidth={2}
                            borderColor="whiteAlpha.200"
                            borderRadius="lg"
                            divider={
                                <StackDivider
                                    borderWidth={1}
                                    borderColor="whiteAlpha.200"
                                />
                            }
                            position="relative"
                        >
                            {/* Header */}
                            <HStack px={4} py={2}>
                                <Text
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="whiteAlpha.500"
                                    textTransform="uppercase"
                                    letterSpacing="wider"
                                >
                                    Audio call
                                </Text>
                            </HStack>

                            {/* Call content */}
                            <HStack align="center" spacing={3} px={4} py={2}>
                                {/* Caller info */}
                                <VStack spacing={-1} align="start" flex={1} minW={0}>
                                    <Text
                                        fontSize="md"
                                        fontWeight="semibold"
                                        color="whiteAlpha.800"
                                        noOfLines={1}
                                    >
                                        {status.caller.label}
                                    </Text>
                                    <Text
                                        fontSize="sm"
                                        color="whiteAlpha.600"
                                        fontWeight="medium"
                                        noOfLines={1}
                                    >
                                        {status.description}
                                    </Text>
                                </VStack>

                                {/* Action buttons */}
                                <HStack spacing={2}>
                                    <Tooltip
                                        placement="top"
                                        bg="gray.50"
                                        color="gray.900"
                                        py={1}
                                        px={2}
                                        borderRadius="md"
                                        openDelay={300}
                                        closeOnClick={true}
                                        shouldWrapChildren={true}
                                        label={status.reject.tooltip.label}
                                    >
                                        <IconButton
                                            {...status.reject.button}
                                            aria-label="Decline call"
                                            size="sm"
                                            colorScheme="red"
                                            variant="solid"
                                            borderRadius="md"
                                            icon={
                                                <Icon
                                                    as={HiOutlinePhone}
                                                    fontSize="md"
                                                    transform="rotate(135deg)"
                                                />
                                            }
                                        />
                                    </Tooltip>
                                    <Tooltip
                                        placement="top"
                                        bg="gray.50"
                                        color="gray.900"
                                        py={1}
                                        px={2}
                                        borderRadius="md"
                                        openDelay={300}
                                        shouldWrapChildren={true}
                                        closeOnClick={true}
                                        label={status.accept.tooltip.label}
                                    >
                                        <IconButton
                                            {...status.accept.button}
                                            aria-label="Accept call"
                                            size="sm"
                                            colorScheme="green"
                                            variant="solid"
                                            borderRadius="md"
                                            icon={
                                                <Icon as={HiOutlinePhone} fontSize="md" />
                                            }
                                        />
                                    </Tooltip>
                                </HStack>
                            </HStack>
                        </VStack>
                        <CallOverlayContainer {...status.call} />
                    </>
                );
            }
            assertNever(status.kind);
        },
        SolutionTool(props) {
            return (
                <Button
                    {...props.button}
                    as={HStack}
                    p={0}
                    bg="none"
                    minH={0}
                    h="inherit"
                    _hover={{ bg: 'whiteAlpha.50' }}
                    borderRadius="lg"
                    borderWidth={2}
                    borderColor="whiteAlpha.200"
                    cursor="pointer"
                    w="full"
                    maxW="24rem"
                    spacing={3}
                    overflow="hidden"
                    userSelect="none"
                >
                    <Center h={8} w={8} bg="whiteAlpha.100" flexShrink={0}>
                        {props.item.icon.kind === 'emoji' ? (
                            <Text fontSize="lg">{props.item.icon.text}</Text>
                        ) : (
                            <Icon as={props.item.icon.Icon} fontSize="lg" />
                        )}
                    </Center>
                    <HStack w="full" justify="space-between" align="center">
                        <Text
                            color="whiteAlpha.800"
                            fontWeight="semibold"
                            noOfLines={1}
                            fontSize="sm"
                        >
                            {props.item.label}
                        </Text>
                        <Badge bg="none" size="xs" px={3} color="whiteAlpha.500">
                            {props.item.type.label}
                        </Badge>
                    </HStack>
                </Button>
            );
        },
    };

    const MessageContent: React.FC<MessageItemViewProps> = (props) => {
        if (props.context.length > 0 && props.parts.length === 0) {
            return (
                <VStack align="start" spacing={1} w="full">
                    {props.context.map((item, index) => (
                        <MessageContextItemContainer
                            key={index}
                            as={MessageContextItemComponents}
                            {...item}
                        />
                    ))}
                </VStack>
            );
        }
        return (
            <VStack align="start" spacing={2} w="full">
                {props.parts.map((part, index) => (
                    <MessagePartContainer
                        key={index}
                        as={MessagePartComponents}
                        {...part}
                    />
                ))}
            </VStack>
        );
    };

    const MessageGroup: React.FC<MessageGroupViewProps> = (props) => {
        const [firstMessage, ...restMessages] = props.messages;
        return (
            <VStack align="stretch" spacing={0} w="full">
                {/* Header */}
                <HStack
                    w="full"
                    align="start"
                    spacing={0}
                    py={1.5}
                    px={2}
                    borderRadius="md"
                    _hover={{ bg: 'whiteAlpha.50' }}
                    position="relative"
                >
                    <HStack align="start" spacing={3} w="full">
                        {props.author.avatar.kind === 'icon' ? (
                            <Center
                                h={6}
                                w={6}
                                flexShrink={0}
                                mt={1}
                                bg="whiteAlpha.100"
                                borderRadius="md"
                            >
                                <Icon
                                    as={props.author.avatar.Icon}
                                    fontSize="sm"
                                    color="whiteAlpha.700"
                                />
                            </Center>
                        ) : (
                            <Avatar
                                size="xs"
                                name={
                                    props.author.role === 'assistant'
                                        ? props.author.avatar.text
                                        : undefined
                                }
                                h={6}
                                w={6}
                                fontSize="2xs"
                                flexShrink={0}
                                mt={1}
                                opacity={0.8}
                                borderRadius="md"
                            />
                        )}
                        <VStack align="start" spacing={1} flex={1} minW={0}>
                            <HStack spacing={2} align="center">
                                <Text
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="whiteAlpha.800"
                                >
                                    {props.author.label}
                                </Text>
                                <Text fontSize="2xs" color="whiteAlpha.600">
                                    {props.timestamp.label}
                                </Text>
                            </HStack>
                            <MessageItemContainer as={MessageContent} {...firstMessage} />
                        </VStack>
                    </HStack>
                </HStack>

                {/* Additional messages in group */}
                {restMessages.map((message) => (
                    <HStack
                        key={message.item.message.id}
                        w="full"
                        align="start"
                        spacing={0}
                        py={1.5}
                        px={2}
                        borderRadius="md"
                        _hover={{ bg: 'whiteAlpha.50' }}
                        position="relative"
                    >
                        <HStack align="start" spacing={3} w="full">
                            {/* Avatar placeholder to maintain alignment */}
                            <Box w={6} h={6} flexShrink={0} />
                            <VStack align="start" spacing={1} flex={1} minW={0}>
                                <MessageItemContainer as={MessageContent} {...message} />
                            </VStack>
                        </HStack>
                    </HStack>
                ))}
            </VStack>
        );
    };

    const MessageSection: React.FC<MessageSectionViewProps> = (props) => {
        return (
            <VStack align="stretch" spacing={0} w="full" position="relative">
                <StickyDateHeader dateLabel={props.label} isSticky={true} />
                <Box position="relative" h="1px" mt="-1rem" mb="1rem">
                    <Divider borderWidth={1} borderColor="whiteAlpha.200" />
                </Box>
                <VStack align="stretch" spacing={0} py={2}>
                    {props.groups.map((item, groupIndex) => {
                        return (
                            <MessageGroupContainer
                                key={groupIndex}
                                as={MessageGroup}
                                {...item}
                            />
                        );
                    })}
                </VStack>
            </VStack>
        );
    };

    return (props) => {
        return (
            <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="black">
                <Grid
                    h="full"
                    w="full"
                    bg="whiteAlpha.50"
                    templateRows="min-content 1fr"
                    gap={{ base: 0 }}
                >
                    {/* Header */}
                    <GridItem
                        p={{ base: 2, md: 2, lg: 2, xl: 3 }}
                        borderBottomWidth={2}
                        borderBottomColor="whiteAlpha.200"
                    >
                        <HStack w="full" justify="space-between" align="center">
                            {/* Left side - User info (like breadcrumbs) */}
                            <HStack spacing={3} align="center">
                                <Box position="relative">
                                    <Avatar
                                        size="sm"
                                        name={props.header.person.name}
                                        h={8}
                                        w={8}
                                        minW={0}
                                        minH={0}
                                        fontWeight="bold"
                                        borderWidth={2}
                                        borderColor="blackAlpha.900"
                                        borderRadius="md"
                                    />
                                    <Box
                                        position="absolute"
                                        bottom={-0.25}
                                        right={-0.5}
                                        w={3}
                                        h={3}
                                        borderRadius="full"
                                        bg="green.400"
                                        borderWidth={2.5}
                                        borderColor="black"
                                    />
                                </Box>
                                <HStack spacing={2} align="center">
                                    <Text
                                        fontWeight="semibold"
                                        fontSize="md"
                                        color="whiteAlpha.900"
                                    >
                                        {props.header.person.name}
                                    </Text>
                                </HStack>
                            </HStack>

                            {/* Right side - Actions */}
                            <HStack spacing={3}>
                                <ButtonGroup
                                    isAttached={true}
                                    borderWidth={1}
                                    borderColor="whiteAlpha.300"
                                    variant="outline"
                                    borderRadius="full"
                                    overflow="hidden"
                                >
                                    <Tooltip
                                        {...props.thread.call.tooltip}
                                        placement="bottom"
                                        bg="gray.50"
                                        color="gray.900"
                                        py={1}
                                        px={2}
                                        borderRadius="md"
                                        shouldWrapChildren={true}
                                    >
                                        <IconButton
                                            {...props.thread.call.button}
                                            // Temporarily disable call button
                                            isDisabled={true}
                                            aria-label={props.thread.call.label}
                                            icon={
                                                <Icon
                                                    as={props.thread.call.Icon}
                                                    fontSize="lg"
                                                />
                                            }
                                            size="sm"
                                            bg="none"
                                            color="whiteAlpha.800"
                                            _hover={{ bg: 'whiteAlpha.50' }}
                                        />
                                    </Tooltip>

                                    <Menu
                                        isOpen={props.more.isOpen}
                                        onClose={props.more.onClose}
                                        onOpen={props.more.onOpen}
                                    >
                                        <MenuButton
                                            as={IconButton}
                                            aria-label="More actions"
                                            icon={<Icon as={HiOutlineDotsHorizontal} />}
                                            size="sm"
                                            bg="none"
                                            color="whiteAlpha.800"
                                            _hover={{ bg: 'whiteAlpha.50' }}
                                        />
                                        <MenuList
                                            bg="black"
                                            borderColor="whiteAlpha.200"
                                            p={0}
                                            overflow="hidden"
                                        >
                                            <VStack
                                                align="start"
                                                w="full"
                                                bg="whiteAlpha.200"
                                                p={1}
                                                spacing={0}
                                            >
                                                {props.more.items.map((item) => (
                                                    <MenuItem
                                                        key={item.id}
                                                        onClick={item.onClick}
                                                        borderRadius="md"
                                                        py={1}
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color={
                                                            item.colorScheme === 'red'
                                                                ? 'red.400'
                                                                : 'whiteAlpha.800'
                                                        }
                                                        _hover={{
                                                            bg:
                                                                item.colorScheme === 'red'
                                                                    ? 'red.500'
                                                                    : 'whiteAlpha.50',
                                                            color:
                                                                item.colorScheme === 'red'
                                                                    ? 'white'
                                                                    : 'whiteAlpha.900',
                                                        }}
                                                    >
                                                        {item.Icon && (
                                                            <Icon as={item.Icon} mr={2} />
                                                        )}
                                                        {item.label}
                                                    </MenuItem>
                                                ))}
                                            </VStack>
                                        </MenuList>
                                    </Menu>
                                </ButtonGroup>
                            </HStack>
                        </HStack>
                    </GridItem>

                    {/* Main Content Area */}
                    <GridItem position="relative" overflow="hidden">
                        <Grid h="full" w="full" templateRows="1fr min-content">
                            {/* Messages */}
                            <GridItem position="relative" overflow="hidden">
                                <VStack
                                    ref={props.thread.messages.container.ref}
                                    onScroll={props.thread.messages.container.onScroll}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    align="stretch"
                                    spacing={0}
                                    pt={4}
                                    pb={{ base: 12, '2xl': 20 }}
                                    overflowY="auto"
                                    bg="whiteAlpha.25"
                                    sx={{
                                        scrollbarGutter: 'stable',
                                        '&::-webkit-scrollbar': {
                                            width: '8px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(255,255,255,0.3)',
                                            borderRadius: '4px',
                                            '&:hover': {
                                                background: 'rgba(255,255,255,0.5)',
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        maxW={{
                                            base: '48rem',
                                            '2xl': '64rem',
                                        }}
                                        mx="auto"
                                        w="full"
                                        px={4}
                                    >
                                        <VStack
                                            align="stretch"
                                            w="full"
                                            py={8}
                                            spacing={4}
                                            // spacing={8}
                                            // divider={
                                            //     <StackDivider
                                            //         borderWidth={1}
                                            //         borderColor="whiteAlpha.200"
                                            //     />
                                            // }
                                        >
                                            {/* Hero Section */}
                                            <VStack align="start" spacing={4}>
                                                <Avatar
                                                    size="lg"
                                                    name={props.header.person.name}
                                                    h={12}
                                                    w={12}
                                                    borderRadius="lg"
                                                />
                                                <VStack align="start" spacing={2}>
                                                    <Text
                                                        fontSize="lg"
                                                        fontWeight="medium"
                                                        color="whiteAlpha.900"
                                                        textAlign="left"
                                                    >
                                                        {props.hero.title}
                                                    </Text>
                                                    <Text
                                                        fontSize="sm"
                                                        color="whiteAlpha.600"
                                                        textAlign="left"
                                                        maxW="md"
                                                    >
                                                        {props.hero.description}
                                                    </Text>
                                                </VStack>
                                                {props.hero.actions.length > 0 && (
                                                    <HStack spacing={2}>
                                                        {props.hero.actions.map(
                                                            (action) => (
                                                                <Button
                                                                    key={action.id}
                                                                    size="sm"
                                                                    borderColor="whiteAlpha.300"
                                                                    borderWidth={1}
                                                                    bg="none"
                                                                    color="whiteAlpha.800"
                                                                    _hover={{
                                                                        bg: 'whiteAlpha.50',
                                                                    }}
                                                                    onClick={
                                                                        action.onClick
                                                                    }
                                                                >
                                                                    {action.label}
                                                                </Button>
                                                            )
                                                        )}
                                                    </HStack>
                                                )}
                                            </VStack>

                                            <VStack
                                                w="full"
                                                align="stretch"
                                                spacing={0}
                                                // divider={
                                                //     <StackDivider
                                                //         borderWidth={1}
                                                //         borderColor="whiteAlpha.50"
                                                //     />
                                                // }
                                                position="relative"
                                            >
                                                {/* Day-grouped messages */}
                                                {props.thread.messages.sections.map(
                                                    (section) => (
                                                        <MessageSectionContainer
                                                            key={section.item.id}
                                                            as={MessageSection}
                                                            {...section}
                                                        />
                                                    )
                                                )}
                                            </VStack>
                                        </VStack>
                                    </Box>
                                </VStack>
                            </GridItem>

                            {/* Composer */}
                            <GridItem
                                p={4}
                                maxW={{ base: '48rem', '2xl': '64rem' }}
                                mx="auto"
                                w="full"
                                bg="whiteAlpha.25"
                                position="relative"
                            >
                                {props.thread.typing.isVisible && (
                                    <TypingIndicator
                                        name={props.thread.typing.authorName}
                                    />
                                )}

                                <ComposerContainer
                                    as={Composer}
                                    {...props.thread.composer}
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                </Grid>
                {props.call && <CallRequestOverlayContainer {...props.call.overlay} />}
            </Box>
        );
    };
}
